import React, { useState, useRef } from 'react';
import { 
    Box, 
    Button, 
    Typography, 
    IconButton, 
    Paper, 
    LinearProgress,
    Tooltip,
    Chip
} from '@mui/material';
import { 
    CloudUpload as UploadIcon,
    Close as CloseIcon,
    InsertDriveFile as FileIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon
} from '@mui/icons-material';

interface EnhancedFileUploaderProps {
    label: string;
    fileName: string;
    fileId: string;
    isLoading: boolean;
    disabled?: boolean;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onClear: () => void;
    validationStatus?: 'valid' | 'invalid' | 'none';
    fileSize?: number;
    processingProgress?: number;
}

const EnhancedFileUploader: React.FC<EnhancedFileUploaderProps> = ({
    label,
    fileName,
    fileId,
    isLoading,
    disabled = false,
    onFileChange,
    onClear,
    validationStatus = 'none',
    fileSize,
    processingProgress
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        if (!disabled && !isLoading) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        
        if (disabled || isLoading) return;

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.type.includes('sheet') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
                // Симулируем событие изменения файла
                const mockEvent = {
                    target: {
                        files: files
                    }
                } as React.ChangeEvent<HTMLInputElement>;
                onFileChange(mockEvent);
            }
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const getStatusIcon = () => {
        switch (validationStatus) {
            case 'valid':
                return <CheckIcon color="success" fontSize="small" />;
            case 'invalid':
                return <ErrorIcon color="error" fontSize="small" />;
            default:
                return <FileIcon color="action" fontSize="small" />;
        }
    };

    const getStatusColor = () => {
        switch (validationStatus) {
            case 'valid':
                return 'success.light';
            case 'invalid':
                return 'error.light';
            default:
                return 'background.paper';
        }
    };

    return (
        <Box sx={{ minWidth: 400 }}>
            <Paper
                elevation={isDragOver ? 4 : 1}
                sx={{
                    p: 2,
                    border: `2px dashed ${isDragOver ? '#1976d2' : '#e0e0e0'}`,
                    borderColor: validationStatus === 'valid' ? 'success.main' : 
                                validationStatus === 'invalid' ? 'error.main' : 
                                isDragOver ? 'primary.main' : 'grey.300',
                    cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
                    bgcolor: getStatusColor(),
                    transition: 'all 0.2s ease-in-out',
                    opacity: disabled ? 0.5 : 1,
                    '&:hover': {
                        borderColor: !disabled && !isLoading ? 'primary.main' : undefined,
                        bgcolor: !disabled && !isLoading ? 'action.hover' : undefined
                    }
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={fileInputRef}
                    id={fileId}
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    onChange={onFileChange}
                    disabled={disabled}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <UploadIcon 
                            sx={{ 
                                fontSize: 40, 
                                color: isDragOver ? 'primary.main' : 'action.active' 
                            }} 
                        />
                        <Typography variant="body2" color="textSecondary">
                            {isDragOver ? 'Отпустите файл' : 'Перетащите файл сюда'}
                        </Typography>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={isLoading || disabled}
                                startIcon={<UploadIcon />}
                            >
                                {label}
                            </Button>
                            
                            {fileName && (
                                <Chip
                                    icon={getStatusIcon()}
                                    label={fileName}
                                    variant="outlined"
                                    size="small"
                                    color={validationStatus === 'valid' ? 'success' : 
                                           validationStatus === 'invalid' ? 'error' : 'default'}
                                />
                            )}
                        </Box>

                        {fileName ? (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="textPrimary" sx={{ flex: 1 }}>
                                        {fileName}
                                    </Typography>
                                    <Tooltip title="Удалить файл">
                                        <IconButton
                                            size="small"
                                            disabled={isLoading}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onClear();
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                
                                {fileSize && (
                                    <Typography variant="caption" color="textSecondary">
                                        {formatFileSize(fileSize)}
                                    </Typography>
                                )}

                                {processingProgress !== undefined && processingProgress > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={processingProgress}
                                            sx={{ height: 6, borderRadius: 3 }}
                                        />
                                        <Typography variant="caption" color="textSecondary">
                                            Обработка: {Math.round(processingProgress)}%
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                Поддерживаемые форматы: .xlsx, .xls
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default EnhancedFileUploader;
